import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Arrow, Stage, Group, Circle, Layer, Rect, Text } from "react-konva";
//import Select from "components/Elements/Select";
import Select from "react-select";
import Button from "components/Elements/Button";
import NiceModal from "@ebay/nice-modal-react";
import { useDispatch, useSelector } from "react-redux";
import { getNodes, downloadWorkflow } from "actions/workflows";
import { v4 as uuidv4 } from "uuid";
import { saveWorkflow, getWorkflowArchives } from "../../services/workflows";
import { dateTimeFormatter } from "../../utils/utils";

const NodeGraphWrapper = styled.div`
  background-color: #eee;
  height: 100%;
  position: relative;
  top: 0;
  left: 0;
  z-index: 200;
  overflow: hidden;
`;

const NodeGraph = ({ workflow, archive, onClose = () => {} }) => {
  const navigate = useNavigate();
  const [dims, setDims] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  console.log("!!! NODE GRAPH ARCHIVE: ", archive);

  if (!workflow.graph || !workflow.graph.nodes || !workflow.graph.edges) {
    workflow.graph = {
      nodes: {},
      edges: [],
    };
  }

  if (!workflow.graph.nodes.entry) {
    workflow.graph.nodes.entry = {
      x: 50,
      y: Math.floor(dims.height / 2) - 25,
      width: 100,
      height: 100,
      name: "INPUT",
      public_id: "entry",
      graph_id: "entry",
      output_params: {
        default: {
          x: 50,
          y: Math.floor(dims.height / 8) - 25,
        },
      },
    };
  }

  if (!workflow.graph.nodes.exit) {
    workflow.graph.nodes.exit = {
      x: dims.width - 420,
      y: Math.floor(dims.height / 2) - 25,
      width: 100,
      height: 100,
      name: "OUTPUT",
      public_id: "exit",
      graph_id: "exit",
      input_params: {
        default: {
          x: dims.width - 100,
          y: Math.floor(dims.height / 8) - 25,
        },
      },
    };
  }

  const nodes = useSelector((state) => state.workflows.nodes);
  const [rerender, setRerender] = useState(Date.now());
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodesByName, setNodesByName] = useState({});
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [selectedInput, setSelectedInput] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const nodeGraphRef = useRef();
  const dispatch = useDispatch();
  const stageRef = useRef();
  const isDraggingRef = useRef(false);
  const lastPointerPositionRef = useRef({ x: 0, y: 0 });
  const scaleBy = 1.05;
  const internalNodeEdges = useRef({});
  const [localGraph, setLocalGraph] = useState(workflow.graph);
  const [archiveOptions, setArchiveOptions] = useState([
    { label: "Live Version", value: 0 },
  ]);

  useEffect(() => {
    // Event handler function
    const handleResize = () => {
      // Handle the resize event here
      const width = nodeGraphRef?.current?.offsetWidth || window.innerWidth;
      const height = nodeGraphRef?.current?.offsetHeight || window.innerHeight;
      setDims({ width, height });
    };

    // Attach the event listener
    window.addEventListener("resize", handleResize);

    if (nodes.length === 0) {
      console.log("!! GETTING NODES");
      dispatch(getNodes(true));
    }

    const stage = stageRef.current;
    const localPositionAndScale = localStorage.getItem(
      `aw_${workflow.public_id}`
    );
    if (localPositionAndScale) {
      const local = JSON.parse(localPositionAndScale);
      if (local) {
        stage.scale(local.scale);
        stage.position(local.position);
      }
    }

    const handleWheel = (e) => {
      e.evt.preventDefault();
      const oldScale = stage.scaleX();

      const mousePointTo = {
        x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
        y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale,
      };
      const newScale =
        e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      stage.scale({ x: newScale, y: newScale });

      const newPos = {
        x:
          -(mousePointTo.x - stage.getPointerPosition().x / newScale) *
          newScale,
        y:
          -(mousePointTo.y - stage.getPointerPosition().y / newScale) *
          newScale,
      };
      stage.position(newPos);
      stage.batchDraw();

      localStorage.setItem(
        `aw_${workflow.public_id}`,
        JSON.stringify({
          scale: { x: newScale, y: newScale },
          position: newPos,
        })
      );
    };

    stage.on("wheel", handleWheel);

    const handleMouseDown = (e) => {
      if (e.evt.button === 1) {
        isDraggingRef.current = true;
        lastPointerPositionRef.current = stage.getPointerPosition();
      }
    };

    const handleMouseMove = (e) => {
      if (isDraggingRef.current) {
        const scale = stage.scaleX();
        const pointerPosition = stage.getPointerPosition();
        const dx =
          (pointerPosition.x - lastPointerPositionRef.current.x) / scale;
        const dy =
          (pointerPosition.y - lastPointerPositionRef.current.y) / scale;

        stage.x(stage.x() + dx);
        stage.y(stage.y() + dy);
        stage.batchDraw();

        lastPointerPositionRef.current = pointerPosition;
      }
    };

    const handleMouseUp = (e) => {
      if (e.evt.button === 1) {
        isDraggingRef.current = false;
      }
    };

    stage.on("mousedown", handleMouseDown);
    stage.on("mousemove", handleMouseMove);
    stage.on("mouseup", handleMouseUp);

    // Load archives
    //
    const loadArchives = async () => {
      const archives = await getWorkflowArchives(workflow);

      const newArchiveOptions = [...archiveOptions];
      console.log("!!! ARCHIVES: ", archives);
      archives.sort((a, b) => {
        return a.archived_at < b.archived_at ? 1 : -1;
      });
      archives.forEach((archive) => {
        newArchiveOptions.push({
          label: (
            <>
              <div style={{ fontWeight: 700 }}>{archive.title}</div>
              <div>
                {dateTimeFormatter.format(new Date(archive.archived_at))}
              </div>
            </>
          ),
          value: archive.archive_id,
        });
      });
      setArchiveOptions(newArchiveOptions);
    };
    loadArchives();

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
      stage.off("wheel", handleWheel);
      stage.off("mousedown", handleMouseDown);
      stage.off("mousemove", handleMouseMove);
      stage.off("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    console.log("!!! ARCHIVE: ", archive);
    console.log("!!! ARCHIVED WORKFLOW: ", workflow);
    setLocalGraph(workflow.graph);
  }, [workflow, archive]);

  useEffect(() => {
    const nodesByName = {};
    nodes.forEach((node) => {
      nodesByName[node.name] = node;
    });
    console.log("!!! NODES: ", nodesByName);
    setNodesByName(nodesByName);
  }, [nodes]);

  useEffect(() => {
    const width = nodeGraphRef?.current?.offsetWidth || window.innerWidth;
    const height = nodeGraphRef?.current?.offsetHeight || window.innerHeight;
    setDims({ width, height });
  }, [nodeGraphRef]);

  useEffect(() => {
    setRerender(Date.now());
  }, [localGraph]);

  // Manage adding or removing edges
  useEffect(() => {
    if (selectedInput && selectedOutput) {
      const graph = { ...localGraph };
      // Check if edge already exists
      const edgeExists = graph.edges.find(
        (edge) =>
          edge.input.graph_id === selectedInput.graph_id &&
          edge.input.key === selectedInput.key &&
          edge.output.graph_id === selectedOutput.graph_id &&
          edge.output.key === selectedOutput.key
      );

      if (edgeExists) {
        // Remove the edge
        graph.edges = graph.edges.filter(
          (edge) =>
            !(
              edge.input.graph_id === selectedInput.graph_id &&
              edge.input.key === selectedInput.key &&
              edge.output.graph_id === selectedOutput.graph_id &&
              edge.output.key === selectedOutput.key
            )
        );
        workflow.graph = graph;
        setLocalGraph(graph);
        setSelectedInput(null);
        setSelectedOutput(null);
        saveWorkflow(workflow);
        return;
      }

      graph.edges.push({
        input: selectedInput,
        output: selectedOutput,
      });
      workflow.graph = graph;
      setLocalGraph(graph);
      setSelectedInput(null);
      setSelectedOutput(null);
      saveWorkflow(workflow);
    }
    // eslint-disable-next-line
  }, [selectedInput, selectedOutput, workflow, archive]);

  const openNodeProperties = (node) => {
    NiceModal.show("node-properties", {
      node_id: node.graph_id,
      workflow: workflow,
    });
  };

  const openEditNode = (node) => {
    console.log("!!! OPEN EDIT NODE: ", node);
    NiceModal.show("add-node", {
      node: node,
      archive_id: workflow.archive_id,
    }).then((updatedNode) => {
      console.log("!!! UPDATED NODE: ", updatedNode);
      for (let key in updatedNode) {
        console.log("!!! UPDATED NODE KEY: ", key);
        node[key] = updatedNode[key];
      }
      node.input_params =
        typeof updatedNode.input_params === "string"
          ? JSON.parse(updatedNode.input_params)
          : updatedNode.input_params;
      node.output_params =
        typeof updatedNode.output_params === "string"
          ? JSON.parse(updatedNode.output_params)
          : updatedNode.output_params;

      console.log("!!! UPDATED LOCAL NODE: ", node);
      updateNodeInGraph(node);
    });
  };

  const addNodeToGraph = (node) => {
    if (node) {
      const newGraphNode = { ...node };
      newGraphNode.graph_id = uuidv4();
      newGraphNode.width = 200;
      newGraphNode.height = 200;
      newGraphNode.x = 160;
      newGraphNode.y = 140 + 10 * Object.keys(localGraph.nodes).length;
      newGraphNode.input_params = JSON.parse(node.input_params);
      newGraphNode.properties = node.properties
        ? JSON.parse(node.properties)
        : {};

      newGraphNode.output_params = {};
      if (node.output_params && node.output_params !== "None") {
        if (node.output_params !== "") {
          newGraphNode.output_params = JSON.parse(node.output_params);
        }

        newGraphNode.functions?.forEach((funct) => {
          if (!newGraphNode.output_params[funct.name]) {
            newGraphNode.output_params[funct.name] = funct;
          }
        });
      }

      if (Object.keys(newGraphNode.output_params).length === 0) {
        newGraphNode.output_params["default"] = {};
      }

      const graph = { ...localGraph };
      graph.nodes[newGraphNode.graph_id] = newGraphNode;
      workflow.graph = graph;
      setLocalGraph(graph);
      saveWorkflow(workflow);
    } else {
      console.log("!!! ADD NODE");
      NiceModal.show("add-node").then((node) => {
        console.log("!!! NEW NODE: ", node);
        const newGraphNode = { ...node };
        newGraphNode.graph_id = uuidv4();
        newGraphNode.width = 200;
        newGraphNode.height = 200;
        newGraphNode.x = 160;
        newGraphNode.y = 140 + 10 * Object.keys(localGraph.nodes).length;
        newGraphNode.input_params = JSON.parse(node.input_params);
        newGraphNode.properties = node.properties
          ? JSON.parse(node.properties)
          : {};

        newGraphNode.output_params = {};
        if (node.output_params && node.output_params !== "None") {
          if (node.output_params !== "") {
            newGraphNode.output_params = JSON.parse(node.output_params);
          }

          newGraphNode.functions.forEach((funct) => {
            if (!newGraphNode.output_params[funct.name]) {
              newGraphNode.output_params[funct.name] = funct;
            }
          });
        }

        if (Object.keys(newGraphNode.output_params).length === 0) {
          newGraphNode.output_params["default"] = {};
        }

        const graph = { ...localGraph };
        graph.nodes[newGraphNode.graph_id] = newGraphNode;
        workflow.graph = graph;
        setLocalGraph(graph);
        saveWorkflow(workflow);
      });
    }
  };

  const removeNodeFromGraph = (node) => {
    const graph = { ...localGraph };
    delete graph.nodes[node.graph_id];
    graph.edges = graph.edges.filter(
      (edge) =>
        edge.input.graph_id !== node.graph_id &&
        edge.output.graph_id !== node.graph_id
    );

    workflow.graph = graph;
    setLocalGraph(graph);
    saveWorkflow(workflow);
  };

  const updateNodeInGraph = (node) => {
    const graph = { ...localGraph };
    graph.nodes[node.graph_id] = node;
    workflow.graph = graph;
    setLocalGraph(graph);
    saveWorkflow(workflow);
  };

  // const removeEdges = () => {
  //   const graph = { ...localGraph };
  //   graph.edges = [];
  //   workflow.graph = graph;
  //   setLocalGraph(graph);
  //   dispatch(saveWorkflow(workflow));
  // };

  const edgeSelected = (edge) => {
    return (
      selectedEdge !== null &&
      selectedEdge.input.graph_id === edge.input.graph_id &&
      selectedEdge.input.key === edge.input.key &&
      selectedEdge.output.graph_id === edge.output.graph_id &&
      selectedEdge.output.key === edge.output.key
    );
  };

  const outputEdge = (node_graph_id, key) => {
    const graph = { ...localGraph };
    const edge = graph.edges.find(
      (edge) =>
        edge.output.graph_id === node_graph_id && edge.output.key === key
    );
    return edge;
  };

  const outputSelected = (node_graph_id, key) => {
    return (
      selectedOutput &&
      selectedOutput.graph_id === node_graph_id &&
      selectedOutput.key === key
    );
  };

  const inputEdge = (node_graph_id, key) => {
    const graph = { ...localGraph };
    const edge = graph.edges.find(
      (edge) => edge.input.graph_id === node_graph_id && edge.input.key === key
    );
    return edge;
  };

  const inputSelected = (node_graph_id, key) => {
    return (
      selectedInput &&
      selectedInput.graph_id === node_graph_id &&
      selectedInput.key === key
    );
  };

  const inputMarkers = (node, inputWidth) => {
    const input_params = node.input_params || {};
    let input_params_offset =
      Object.keys(input_params).length > 0
        ? node.height / Object.keys(input_params).length
        : 0;

    const input_markers = [];
    const input_param_keys = Object.keys(input_params);
    input_param_keys.sort((a, b) => {
      return a < b ? -1 : 1;
    });
    input_param_keys.forEach((input_param_key, i) => {
      const x = 0;
      const y = i * input_params_offset + input_params_offset / 2;
      node.input_params[input_param_key].x = node.x + x;
      node.input_params[input_param_key].y = node.y + y;

      input_markers.push(
        <Group x={x} y={y} key={`input_param_${i}`}>
          <Circle
            key={i}
            x={0}
            y={0}
            radius={6}
            fill={
              inputSelected(node.graph_id, input_param_key)
                ? "#0f0"
                : inputEdge(node.graph_id, input_param_key) !== undefined
                ? "#000"
                : "#fff"
            }
            stroke="#000"
            strokeWidth={1}
            onClick={() => {
              if (inputSelected(node.graph_id, input_param_key)) {
                setSelectedInput(null);
              } else {
                setSelectedInput({
                  graph_id: node.graph_id,
                  key: input_param_key,
                });
              }
            }}
          />
          <Text
            x={10}
            y={-5}
            width={inputWidth}
            fontSize={12}
            fontStyle="bold"
            text={input_param_key}
            align="left"
          />
        </Group>
      );
    });
    return input_markers;
  };

  const getOutputParams = (node) => {
    const output_params = node.output_params || {};
    if (node.functions) {
      node.functions.forEach((funct) => {
        if (funct.output_params) {
          try {
            const funct_output_params = JSON.parse(funct.output_params);
            Object.keys(funct_output_params).forEach(
              (funct_output_param_key) => {
                output_params[funct_output_param_key] =
                  funct_output_params[funct_output_param_key];
              }
            );
          } catch (e) {
            console.log("!!! ERROR: ", e);
          }
        } else {
          output_params[funct["name"]] = { type: "string" };
        }
      });
    }

    return output_params;
  };

  const outputMarkers = (node, outputWidth) => {
    const output_params = getOutputParams(node);

    const output_markers = [];
    let output_params_offset =
      Object.keys(output_params).length > 0
        ? node.height / Object.keys(output_params).length
        : 0;
    const output_param_keys = Object.keys(output_params);
    output_param_keys.sort((a, b) => {
      if (a === "default") {
        return 1;
      }
      if (b === "default") {
        return -1;
      }
      return a < b ? -1 : 1;
    });

    output_param_keys.forEach((output_param_key, i) => {
      const x = node.width;
      const y = i * output_params_offset + output_params_offset / 2;

      output_params[output_param_key].x = node.x + node.width;

      node.output_params[output_param_key].x = node.x + x;
      node.output_params[output_param_key].y = node.y + y;

      output_markers.push(
        <Group x={x} y={y} key={i}>
          <Circle
            key={`circle_${i}`}
            x={0}
            y={0}
            radius={6}
            fill={
              outputSelected(node.graph_id, output_param_key)
                ? "#0f0"
                : outputEdge(node.graph_id, output_param_key) !== undefined
                ? "#000"
                : "#fff"
            }
            stroke="#000"
            strokeWidth={1}
            onClick={(e) => {
              e.cancelBubble = true;
              if (outputSelected(node.graph_id, output_param_key)) {
                setSelectedOutput(null);
              } else {
                setSelectedOutput({
                  graph_id: node.graph_id,
                  key: output_param_key,
                });
              }
            }}
          />
          <Text
            key={`text_${i}`}
            x={-outputWidth - 10}
            y={-5}
            width={outputWidth}
            fontSize={12}
            fontStyle="bold"
            text={output_param_key}
            align="right"
          />
        </Group>
      );
    });

    return output_markers;
  };

  const renderGraphNodes = () => {
    return Object.keys(localGraph.nodes).map((node_id) => {
      const node = localGraph.nodes[node_id];
      const edges = internalNodeEdges.current[node.graph_id] || [];
      const outputParams = getOutputParams(node);
      const inputParams = node.input_params || {};

      node.height = Math.max(
        140,
        30 * Object.keys(outputParams).length,
        30 * Object.keys(inputParams).length
      );

      let longestOutput = 0;
      let longestInput = 0;
      Object.keys(outputParams).forEach((key) => {
        if (key.length > longestOutput) {
          longestOutput = key.length;
        }
      });

      Object.keys(inputParams).forEach((key) => {
        if (key.length > longestInput) {
          longestInput = key.length;
        }
      });

      let width = longestOutput * 8 + longestInput * 8 + 30;
      node.width = Math.max(140, width);

      return (
        <Group
          key={node_id}
          x={node.x}
          y={node.y}
          width={node.width}
          height={node.height}
          draggable={true}
          onMouseOver={() => {
            document.body.style.cursor = "pointer";
          }}
          onMouseOut={() => {
            document.body.style.cursor = "default";
          }}
          onDragMove={(e) => {
            const graph = { ...localGraph };
            graph.nodes[node_id].x = e.target.x();
            graph.nodes[node_id].y = e.target.y();
            workflow.graph = graph;
            setLocalGraph(graph);
          }}
          onDragEnd={(e) => {
            const graph = { ...localGraph };
            graph.nodes[node_id].x = e.target.x();
            graph.nodes[node_id].y = e.target.y();
            workflow.graph = graph;
            setLocalGraph(graph);
            saveWorkflow(workflow);
          }}
        >
          <Rect
            cornerRadius={10}
            shadowBlur={10}
            width={node.width}
            height={node.height}
            fill="#fff"
          />
          <Text
            x={0}
            y={node.name.length > 18 ? -36 : -20}
            text={node.name}
            width={node.width - 10}
            fontSize={14}
            fontStyle="bold"
            align="center"
          />
          {node.public_id !== "entry" && node.public_id !== "exit" && (
            <>
              <Text
                text="X"
                x={20}
                y={10}
                fontSize={14}
                fontStyle="bold"
                align="center"
                onMouseOver={() => {
                  document.body.style.cursor = "pointer";
                }}
                onMouseOut={() => {
                  document.body.style.cursor = "default";
                }}
                onClick={() => {
                  removeNodeFromGraph(node);
                }}
              />
              <Text
                text="✎"
                x={10}
                y={node.height - 25}
                fontSize={22}
                fontStyle="bold"
                align="center"
                onMouseOver={() => {
                  document.body.style.cursor = "pointer";
                }}
                onMouseOut={() => {
                  document.body.style.cursor = "default";
                }}
                onClick={() => {
                  openEditNode(node);
                }}
              />
            </>
          )}
          {node.properties && Object.keys(node.properties).length > 0 && (
            <Text
              text="⚙"
              x={node.width - 20}
              y={node.height - 25}
              fontSize={22}
              fontStyle="bold"
              align="center"
              onMouseOver={() => {
                document.body.style.cursor = "pointer";
              }}
              onMouseOut={() => {
                document.body.style.cursor = "default";
              }}
              onClick={() => {
                openNodeProperties(node);
              }}
            />
          )}
          {edges.map((edge, index) => {
            const input_node = node;
            const output_node = node;
            const output_param = output_node.output_params[edge.output.key];
            const input_param = input_node.input_params[edge.input.key];
            const output_points = {
              x: output_param.x - node.x,
              y: output_param.y - node.y,
            };
            const input_points = {
              x: input_param.x - node.x,
              y: input_param.y - node.y,
            };
            return (
              <Arrow
                key={index}
                points={[
                  output_points.x,
                  output_points.y,
                  input_points.x,
                  input_points.y,
                ]}
                fill="#999"
                stroke="#999"
                strokeWidth={edgeSelected(edge) ? 5 : 3}
                dash={[10, 10]}
                pointerLength={12}
                pointerWidth={12}
              />
            );
          })}
          {inputMarkers(node, longestInput * 8)}
          {outputMarkers(node, longestOutput * 8)}
        </Group>
      );
    });
  };

  const renderGraphEdges = () => {
    internalNodeEdges.current = {};
    const e = localGraph.edges.map((edge, index) => {
      const input_points = { x: 0, y: 0 };
      const output_points = { x: 0, y: 0 };
      const same_node = edge.input.graph_id === edge.output.graph_id;
      // Skip for now since it needs to be drawn on top of the node
      if (same_node) {
        if (internalNodeEdges.current[edge.input.graph_id] === undefined) {
          internalNodeEdges.current[edge.input.graph_id] = [];
        }
        internalNodeEdges.current[edge.input.graph_id].push(edge);
        return null;
      }

      try {
        const output = edge.output;
        const output_node = localGraph.nodes[output.graph_id];
        const output_param = output_node.output_params[output.key];
        output_points.x = output_param.x;
        output_points.y = output_param.y;

        const input = edge.input;
        const input_node = localGraph.nodes[input.graph_id];
        const input_param = input_node.input_params[input.key];
        input_points.x = input_param.x;
        input_points.y = input_param.y;
      } catch (e) {
        return null;
      }

      return (
        <Group
          key={index}
          onClick={(e) => {
            e.cancelBubble = true;
            if (edgeSelected(edge)) {
              setSelectedEdge(null);
            } else {
              setSelectedEdge(edge);
            }
          }}
          onMouseOver={() => {
            document.body.style.cursor = "pointer";
          }}
          onMouseOut={() => {
            document.body.style.cursor = "default";
          }}
        >
          <Arrow
            key={index}
            points={[
              output_points.x,
              output_points.y,
              input_points.x,
              input_points.y,
            ]}
            fill="#000"
            stroke="#000"
            strokeWidth={edgeSelected(edge) ? 5 : 3}
            pointerWidth={12}
            pointerLength={12}
          />
        </Group>
      );
    });
    return e;
  };

  const runWorkflow = (workflow) => {
    NiceModal.show("test-workflow", { workflow_id: workflow.public_id });
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        display: "flex",
        flexDirection: "row",
      }}
    >
      <NodeGraphWrapper ref={nodeGraphRef}>
        <div
          style={{
            position: "absolute",
            left: "20px",
            top: "30px",
            fontSize: "30px",
            fontWeight: "bold",
            zIndex: 100,
          }}
        >
          {workflow.name} Node Graph
        </div>
        <div
          style={{
            position: "absolute",
            right: "20px",
            top: "30px",
            zIndex: 100,
          }}
        >
          <Button onClick={() => onClose()}>
            <i className="fa fa-times text-sm cursor-pointer" />
          </Button>
        </div>

        <div
          style={{
            position: "absolute",
            left: "20px",
            top: "80px",
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            flexGrow: 1,
            zIndex: 100,
          }}
        >
          {
            <div style={{ flexGrow: 1, width: "300px" }}>
              <Select
                label="Nodes"
                placeholder="Add a node"
                name="nodes"
                options={
                  nodes
                    ? [
                        { label: "New Node", value: "New Node" },
                        ...nodes.map((node) => {
                          return { label: node.name, value: node.name };
                        }),
                      ]
                    : []
                }
                value={
                  selectedNode
                    ? { label: selectedNode.name, value: selectedNode.name }
                    : null
                }
                onChange={(node) => {
                  setSelectedNode(nodesByName[node.value]);
                }}
              />
            </div>
          }
          <Button
            style={{ flexGrow: 0, marginLeft: "10px" }}
            onClick={() => addNodeToGraph(selectedNode)}
            title={"Add Node"}
          >
            <i className="fa fa-plus text-sm cursor-pointer" />
          </Button>
          <Button
            style={{ flexGrow: 0, marginLeft: "10px" }}
            onClick={() => runWorkflow(workflow)}
            title={"Run Workflow"}
          >
            <i className="fa fa-play text-sm cursor-pointer" />
          </Button>
          <Button
            style={{ flexGrow: 0, marginLeft: "10px" }}
            onClick={() => {
              const stage = stageRef.current;
              stage.position({ x: 0, y: 0 });
              stage.scale({ x: 1, y: 1 });

              localStorage.removeItem(`aw_${workflow.public_id}`);
            }}
            title={"Reset View"}
          >
            <i className="fa fa--refresh text-sm cursor-pointer" />
          </Button>
          <Button
            style={{ flexGrow: 0, marginLeft: "10px" }}
            onClick={() => downloadWorkflow(workflow.public_id, workflow.name)}
            title={"Download Workflow"}
          >
            <i className="fa fa-download text-sm cursor-pointer" />
          </Button>
          <Button
            style={{ flexGrow: 0, marginLeft: "10px" }}
            onClick={() => NiceModal.show("archive-workflow", { workflow })}
            title={"Archive Workflow"}
          >
            <i className="fa fa-archive text-sm cursor-pointer" />
          </Button>
          <div style={{ flexGrow: 1, width: "300px" }}>
            <Select
              label="Archives"
              placeholder="Live Version"
              name="archives"
              options={archiveOptions}
              onChange={(archive) => {
                if (archive.value === 0) {
                  navigate(`/workflow/${workflow.public_id}/graph`);
                  return;
                }
                navigate(
                  `/workflow/${workflow.public_id}/graph/${archive.value}`
                );
              }}
            />
          </div>
        </div>
        <Stage ref={stageRef} width={dims.width} height={dims.height}>
          <Layer>
            {renderGraphEdges(rerender)}
            {renderGraphNodes(rerender)}
          </Layer>
        </Stage>
      </NodeGraphWrapper>
    </div>
  );
};

export default NodeGraph;
